import { useState } from "react";
import * as Icons from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { toast } from "sonner";
import { COUNTRIES } from "@/lib/countries";

const LANGS = [["en", "English"], ["es", "Spanish"], ["th", "Thai"]];
const METHODS = [["email", "Email"], ["phone", "Phone"], ["portal", "Portal Messages"], ["whatsapp", "WhatsApp"]];
const LANG_LABEL = Object.fromEntries(LANGS);
const METHOD_LABEL = Object.fromEntries(METHODS);

const inputCls = "w-full bg-[#F6F7FB] border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#171B2E] focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40";
const ADDR_KEYS = ["line1", "line2", "city", "region", "postal_code", "country"];

function LabeledField({ label, htmlFor, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function ReadRow({ label, value, testid, multiline }) {
  const has = value != null && value !== "";
  return (
    <div data-testid={testid}>
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</div>
      {has
        ? <div className={`mt-1 text-sm text-[#171B2E] font-medium break-words ${multiline ? "whitespace-pre-line" : ""}`}>{value}</div>
        : <div className="mt-1 text-sm text-slate-400 italic">Not provided</div>}
    </div>
  );
}

function ProfilePhoto({ profile, setProfile }) {
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(Date.now());
  const initials = (profile?.full_name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("");
  const token = localStorage.getItem("taxflow_portal_token");
  const src = profile?.photo_url
    ? `${process.env.REACT_APP_BACKEND_URL}${profile.photo_url}?auth=${token}&t=${bust}`
    : null;

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast.error("Image too large (max 6MB)."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await portalApi.post("/portal/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile(data); setBust(Date.now());
      toast.success("Photo updated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not upload photo.");
    } finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <label className="relative h-14 w-14 shrink-0 cursor-pointer group" data-testid="portal-photo-upload">
      {src
        ? <img src={src} alt="" className="h-14 w-14 rounded-2xl object-cover border border-slate-200" />
        : <div className="h-14 w-14 rounded-2xl bg-[#171B2E] text-white flex items-center justify-center text-lg font-bold">{initials}</div>}
      <div className="absolute inset-0 rounded-2xl bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        {busy ? <Icons.Loader2 className="h-5 w-5 text-white animate-spin" /> : <Icons.Camera className="h-5 w-5 text-white" />}
      </div>
      <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onPick} data-testid="portal-photo-input" />
    </label>
  );
}

export default function PortalProfile() {
  const { profile, setProfile } = usePortalAuth();
  const ci = profile?.contact_information || {};
  const addr0 = ci.mailing_address || {};

  const seed = () => ({
    mobile_phone: ci.mobile_phone || "",
    home_phone: ci.home_phone || "",
    whatsapp: ci.whatsapp || "",
    line_id: ci.line_id || "",
    line1: addr0.line1 || "", line2: addr0.line2 || "", city: addr0.city || "",
    region: addr0.region || "", postal_code: addr0.postal_code || "", country: addr0.country || "",
    preferred_language: profile?.preferred_language || "en",
    preferred_contact_method: profile?.preferred_contact_method || "email",
    time_zone: profile?.time_zone || "",
  });

  const [editing, setEditing] = useState(false);
  const [initial, setInitial] = useState(seed);
  const [form, setForm] = useState(seed);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const startEdit = () => { const s = seed(); setInitial(s); setForm(s); setEditing(true); };
  const cancel = () => { setEditing(false); setTimeout(() => document.querySelector('[data-testid="profile-edit-btn"]')?.focus(), 0); };

  const buildPayload = () => {
    const p = {};
    ["mobile_phone", "home_phone", "whatsapp", "line_id", "preferred_language", "preferred_contact_method", "time_zone"]
      .forEach((k) => { const cur = (form[k] || "").trim(); if (cur !== (initial[k] || "").trim()) p[k] = cur; });
    if (ADDR_KEYS.some((k) => (form[k] || "").trim() !== (initial[k] || "").trim())) {
      p.mailing_address = ADDR_KEYS.reduce((o, k) => { const v = (form[k] || "").trim(); if (v) o[k] = v; return o; }, {});
    }
    return p;
  };

  const save = async () => {
    const payload = buildPayload();
    if (Object.keys(payload).length === 0) { setEditing(false); return; }
    setBusy(true);
    try {
      const { data } = await portalApi.patch("/portal/profile", payload);
      setProfile(data);
      setEditing(false);
      toast.success("Contact information updated.");
      setTimeout(() => document.querySelector('[data-testid="profile-edit-btn"]')?.focus(), 0);
    } catch (err) {
      const s = err?.response?.status;
      if (s === 409) toast.error("Your contact information changed while you were editing it. Refresh and try again.");
      else if (s === 400 || s === 422) toast.error("Please review the highlighted fields.");
      else toast.error("We could not save your changes. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="portal-profile">
      <div>
        <h1 className="font-heading text-3xl font-bold text-[#171B2E]">Account &amp; Preferences</h1>
        <p className="text-slate-500 text-sm mt-1">Manage your contact information, communication preferences, time zone and portal settings.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-4 pb-5 border-b border-slate-100">
          <ProfilePhoto profile={profile} setProfile={setProfile} />
          <div className="min-w-0 flex-1">
            <div className="font-heading text-lg font-semibold text-[#171B2E]">{profile?.full_name}</div>
            <div className="text-sm text-slate-500 truncate">{profile?.email} · <span className="font-mono text-xs">{profile?.client_number}</span></div>
            <div className="text-xs text-slate-400 mt-0.5">Click your photo to upload a new one (optional).</div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-5 mb-4">
          <h2 className="font-heading font-semibold text-[#171B2E]">Contact Information</h2>
          {!editing && (
            <button onClick={startEdit} data-testid="profile-edit-btn"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#171B2E] border border-slate-300 rounded-lg px-3 py-1.5 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40">
              <Icons.Pencil className="h-3.5 w-3.5" /> Edit
            </button>
          )}
        </div>

        {editing ? (
          <div className="space-y-5" data-testid="profile-edit-form">
            <LabeledField label="Email">
              <div className="text-sm text-[#171B2E] font-medium">{profile?.email}</div>
              <p className="text-xs text-slate-400 mt-1">The email used to access your portal must be changed by your tax team.</p>
            </LabeledField>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <LabeledField label="Mobile Phone" htmlFor="pf-mobile"><input id="pf-mobile" value={form.mobile_phone} onChange={set("mobile_phone")} className={inputCls} data-testid="profile-mobile-phone" placeholder="+66 ..." /></LabeledField>
              <LabeledField label="Home Phone" htmlFor="pf-home"><input id="pf-home" value={form.home_phone} onChange={set("home_phone")} className={inputCls} data-testid="profile-home-phone" placeholder="+1 ..." /></LabeledField>
              <LabeledField label="WhatsApp" htmlFor="pf-wa"><input id="pf-wa" value={form.whatsapp} onChange={set("whatsapp")} className={inputCls} data-testid="profile-whatsapp" /></LabeledField>
              <LabeledField label="LINE" htmlFor="pf-line"><input id="pf-line" value={form.line_id} onChange={set("line_id")} className={inputCls} data-testid="profile-line" /></LabeledField>
            </div>

            <fieldset className="rounded-xl border border-slate-200 p-4">
              <legend className="text-xs font-medium text-slate-500 uppercase tracking-wide px-1">Mailing Address</legend>
              <div className="space-y-4">
                <LabeledField label="Address Line 1" htmlFor="pf-l1"><input id="pf-l1" value={form.line1} onChange={set("line1")} className={inputCls} data-testid="profile-line1" /></LabeledField>
                <LabeledField label="Address Line 2" htmlFor="pf-l2"><input id="pf-l2" value={form.line2} onChange={set("line2")} className={inputCls} data-testid="profile-line2" /></LabeledField>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <LabeledField label="City" htmlFor="pf-city"><input id="pf-city" value={form.city} onChange={set("city")} className={inputCls} data-testid="profile-city" /></LabeledField>
                  <LabeledField label="State / Province / Region" htmlFor="pf-region"><input id="pf-region" value={form.region} onChange={set("region")} className={inputCls} data-testid="profile-region" /></LabeledField>
                  <LabeledField label="Postal Code" htmlFor="pf-postal"><input id="pf-postal" value={form.postal_code} onChange={set("postal_code")} className={inputCls} data-testid="profile-postal-code" /></LabeledField>
                  <LabeledField label="Country" htmlFor="pf-country">
                    <select id="pf-country" value={form.country} onChange={set("country")} className={inputCls} data-testid="profile-country">
                      <option value="">Select country</option>
                      {COUNTRIES.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </LabeledField>
                </div>
              </div>
            </fieldset>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <LabeledField label="Time Zone" htmlFor="pf-tz"><input id="pf-tz" value={form.time_zone} onChange={set("time_zone")} className={inputCls} data-testid="profile-timezone" placeholder="e.g. Asia/Bangkok" /></LabeledField>
              <LabeledField label="Preferred Language" htmlFor="pf-lang">
                <select id="pf-lang" value={form.preferred_language} onChange={set("preferred_language")} className={inputCls} data-testid="profile-language">
                  {LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </LabeledField>
              <LabeledField label="Contact Preference" htmlFor="pf-method">
                <select id="pf-method" value={form.preferred_contact_method} onChange={set("preferred_contact_method")} className={inputCls} data-testid="profile-method">
                  {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </LabeledField>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={cancel} disabled={busy} data-testid="profile-cancel"
                className="inline-flex items-center gap-2 border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-lg px-4 py-2.5 text-sm font-medium disabled:opacity-60">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="profile-save"
                className="inline-flex items-center gap-2 bg-[#F5A623] hover:bg-[#e0951a] disabled:opacity-60 text-[#171B2E] font-semibold rounded-lg px-5 py-2.5 text-sm">
                {busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Save className="h-4 w-4" />} Save Changes
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="profile-display">
            <ReadRow label="Email" value={ci.email} testid="profile-view-email" />
            <div className="hidden sm:block" />
            <ReadRow label="Mobile Phone" value={ci.mobile_phone} testid="profile-view-mobile" />
            <ReadRow label="Home Phone" value={ci.home_phone} testid="profile-view-home" />
            <div className="sm:col-span-2"><ReadRow label="Mailing Address" value={ci.formatted_mailing_address} multiline testid="profile-view-address" /></div>
            <ReadRow label="WhatsApp" value={ci.whatsapp} testid="profile-view-whatsapp" />
            <ReadRow label="LINE" value={ci.line_id} testid="profile-view-line" />
            <ReadRow label="Time Zone" value={profile?.time_zone} testid="profile-view-timezone" />
            <ReadRow label="Preferred Language" value={LANG_LABEL[profile?.preferred_language] || profile?.preferred_language} testid="profile-view-language" />
            <ReadRow label="Contact Preference" value={METHOD_LABEL[profile?.preferred_contact_method] || profile?.preferred_contact_method} testid="profile-view-method" />
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-3"><Icons.ShieldCheck className="h-5 w-5 text-[#F5A623]" /><h2 className="font-heading font-semibold text-[#171B2E]">Security</h2></div>
        <p className="text-sm text-slate-500">Your portal is protected with bank-grade encryption. To reset your password, contact your tax advisor and they'll send you a secure reset link.</p>
      </div>
    </div>
  );
}
